import { useMutation } from "@apollo/client";
import ReportProblem from "@mui/icons-material/ReportProblem";
import SignalCellularAlt from "@mui/icons-material/SignalCellularAlt";
import SignalCellularAlt1Bar from "@mui/icons-material/SignalCellularAlt1Bar";
import SignalCellularAlt2Bar from "@mui/icons-material/SignalCellularAlt2Bar";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { LinearIcon } from "components/LinearIcon";
import { gql } from "generated-graphql";
import { ReactNode, useCallback, useEffect, useState } from "react";

export const CREATE_LINEAR_ISSUE_MUTATION = gql(`
  mutation CreateLinearIssueFromDialog($input: CreateLinearIssueInput!) {
    createLinearIssue(input: $input) {
      success
      issueIdentifier
      issueUrl
      issueId
    }
  }
`);

export interface CreateLinearIssueResult {
  issueIdentifier: string;
  issueUrl: string | null;
  issueId: string | null;
}

interface CreateLinearIssueDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-populated title for the Linear ticket */
  defaultTitle: string;
  /** Called at creation time to build the ticket description */
  buildDescription: () => string;
  /** Optional callback after successful creation (e.g., to tag workflows) */
  onCreated?: (result: CreateLinearIssueResult) => void | Promise<void>;
  /** Optional content rendered before the title field (e.g., cluster alert) */
  alertContent?: ReactNode;
  /** Content rendered in the preview box */
  previewContent: ReactNode;
  /** Optional content rendered after the preview box (e.g., tagging note) */
  footerContent?: ReactNode;
}

export function CreateLinearIssueDialog({
  open,
  onClose,
  defaultTitle,
  buildDescription,
  onCreated,
  alertContent,
  previewContent,
  footerContent,
}: CreateLinearIssueDialogProps) {
  const alerts = useAlerts();
  const [isCreating, setIsCreating] = useState(false);
  const [linearTicketTitle, setLinearTicketTitle] = useState("");
  const [linearTicketStatus, setLinearTicketStatus] = useState("Triage");
  const [linearTicketPriority, setLinearTicketPriority] = useState(0);

  const [createLinearIssue] = useMutation(CREATE_LINEAR_ISSUE_MUTATION);

  useEffect(() => {
    if (!open) return;
    setLinearTicketTitle(defaultTitle);
    setLinearTicketStatus("Triage");
    setLinearTicketPriority(0);
  }, [open, defaultTitle]);

  const handleConfirm = useCallback(async () => {
    setIsCreating(true);

    try {
      const description = buildDescription();

      const result = await createLinearIssue({
        variables: {
          input: {
            title: linearTicketTitle,
            description,
            status: linearTicketStatus,
            priority: linearTicketPriority,
          },
        },
      });

      const issueIdentifier = result.data?.createLinearIssue?.issueIdentifier;
      const issueUrl = result.data?.createLinearIssue?.issueUrl ?? null;
      const issueId = result.data?.createLinearIssue?.issueId ?? null;

      if (!issueIdentifier) {
        throw new Error("Failed to get issue identifier from Linear");
      }

      alerts.success(`Linear ticket ${issueIdentifier} created successfully!`);

      if (onCreated) {
        await onCreated({ issueIdentifier, issueUrl, issueId });
      }

      if (issueUrl) {
        window.open(issueUrl, "_blank", "noopener");
      }

      onClose();
    } catch (err) {
      alerts.error("Failed to create Linear ticket", err);
    } finally {
      setIsCreating(false);
    }
  }, [
    buildDescription,
    createLinearIssue,
    alerts,
    onClose,
    onCreated,
    linearTicketTitle,
    linearTicketStatus,
    linearTicketPriority,
  ]);

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      header="Create Linear Ticket"
      confirmText="Create Linear Ticket"
      cancelText="Cancel"
      loading={isCreating}
      confirmStartIcon={<LinearIcon />}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          A Linear ticket will be created in the Engineering Support project with the following
          information:
        </Typography>

        {alertContent}

        <TextField
          label="Title"
          value={linearTicketTitle}
          onChange={(e) => setLinearTicketTitle(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            alignItems: "start",
            mb: 2,
          }}
        >
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={linearTicketPriority}
              onChange={(e) => setLinearTicketPriority(e.target.value as number)}
              label="Priority"
            >
              <MenuItem value={0}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        width: 24,
                        textAlign: "center",
                      }}
                    >
                      ---
                    </Typography>
                    <Typography>No priority</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    0
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={1}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ReportProblem fontSize="small" />
                    <Typography>Urgent</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    1
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SignalCellularAlt fontSize="small" />
                    <Typography>High</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    2
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={3}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ position: "relative", width: 24, height: 24 }}>
                      <SignalCellularAlt
                        fontSize="small"
                        sx={{ position: "absolute", inset: 0, opacity: 0.18 }}
                      />
                      <SignalCellularAlt2Bar
                        fontSize="small"
                        sx={{ position: "absolute", inset: 0 }}
                      />
                    </Box>
                    <Typography>Medium</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    3
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={4}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ position: "relative", width: 24, height: 24 }}>
                      <SignalCellularAlt
                        fontSize="small"
                        sx={{ position: "absolute", inset: 0, opacity: 0.18 }}
                      />
                      <SignalCellularAlt1Bar
                        fontSize="small"
                        sx={{ position: "absolute", inset: 0 }}
                      />
                    </Box>
                    <Typography>Low</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    4
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Status</FormLabel>
            <RadioGroup
              value={linearTicketStatus}
              onChange={(e) => setLinearTicketStatus(e.target.value)}
              row
            >
              <FormControlLabel value="Triage" control={<Radio />} label="Triage" />
              <FormControlLabel value="Todo" control={<Radio />} label="Todo" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box
          sx={{
            bgcolor: "action.hover",
            borderRadius: 1,
            p: 2,
          }}
        >
          {previewContent}
        </Box>

        {footerContent}
      </Stack>
    </ConfirmDialog>
  );
}
